/**
 * The first-run onboarding gate (F-NAV-16 … F-NAV-22).
 *
 * It replaces the whole shell rather than sitting inside it, which is why it
 * renders before the four destination roots exist: nothing behind it should be
 * mounted, scrolled or drafted while the user has not yet said they are ready.
 *
 * Skip is on every page and completes without visiting the later ones. That is
 * a deliberate product rule, not an oversight — every page here is explanatory,
 * and the only thing onboarding was ever allowed to *request* (notification
 * permission, F-NAV-20) does not exist on the web at all (§9.2).
 */

import type { ReactNode } from "react";
import { useState } from "react";
import { Button, Container, Text, VisuallyHidden, useAccessibilityTextSize } from "@ff/ui";
import {
  CALLS_PAGE,
  CUES_PAGE,
  FORMAT_PAGE,
  ONBOARDING_FOOTER,
  ONBOARDING_PAGE_COUNT,
  ONBOARDING_TITLE,
  SAFETY_PAGE,
  pageIndicatorLabel,
  punchCallLabel,
} from "./copy";
import type { OnboardingRow } from "./copy";
import styles from "./Onboarding.module.css";

export interface OnboardingFlowProps {
  readonly onComplete: () => void;
}

function PageHeader({
  eyebrow,
  title,
  detail,
  accessibilityTextSize,
}: {
  readonly eyebrow: string;
  readonly title: string;
  readonly detail: string;
  readonly accessibilityTextSize: boolean;
}): ReactNode {
  return (
    <header className={styles.pageHeader}>
      <Text variant="caption2" tone="tertiary" uppercase as="div">
        {eyebrow}
      </Text>
      {/* Tracking is −1 normally and 0 at accessibility sizes: negative
          tracking on already-large glyphs is what turns a headline into a
          smear for a low-vision reader. */}
      <Text
        variant="largeTitle"
        weight="bold"
        as="h1"
        className={accessibilityTextSize ? styles.titleLoose : styles.titleTight}
      >
        {title}
      </Text>
      <Text variant="body" tone="secondary" as="p">
        {detail}
      </Text>
    </header>
  );
}

function RowList({ rows }: { readonly rows: readonly OnboardingRow[] }): ReactNode {
  return (
    <dl className={styles.rows}>
      {rows.map((row) => (
        <div key={row.title} className={styles.row}>
          <Text variant="headline" as="dt">
            {row.title}
          </Text>
          <Text variant="subheadline" tone="secondary" as="dd">
            {row.detail}
          </Text>
        </div>
      ))}
    </dl>
  );
}

function FormatPage({ accessibilityTextSize }: { readonly accessibilityTextSize: boolean }) {
  return (
    <>
      <PageHeader
        eyebrow={FORMAT_PAGE.eyebrow}
        title={FORMAT_PAGE.title}
        detail={FORMAT_PAGE.detail}
        accessibilityTextSize={accessibilityTextSize}
      />
      <RowList rows={FORMAT_PAGE.rows} />
    </>
  );
}

function CallsPage({ accessibilityTextSize }: { readonly accessibilityTextSize: boolean }) {
  return (
    <>
      <PageHeader
        eyebrow={CALLS_PAGE.eyebrow}
        title={CALLS_PAGE.title}
        detail={CALLS_PAGE.detail}
        accessibilityTextSize={accessibilityTextSize}
      />
      {/* Two columns normally, one at accessibility sizes (§8.1's structural
          branch): a two-column grid of numbered calls becomes unreadable long
          before the type stops fitting. */}
      <ul
        className={styles.punchMap}
        data-ff-columns={accessibilityTextSize ? "1" : "2"}
        data-testid="onboarding.punchMap"
      >
        {CALLS_PAGE.punchMap.map((call) => (
          <li key={call.number} className={styles.punch}>
            {/* One accessibility element per row: "Punch 1, Jab, Lead straight"
                rather than three fragments a screen reader has to reassemble. */}
            <VisuallyHidden>{punchCallLabel(call)}</VisuallyHidden>
            <span aria-hidden="true" className={styles.punchNumber}>
              {call.number}
            </span>
            <span aria-hidden="true" className={styles.punchName}>
              {call.name}
            </span>
            <span aria-hidden="true" className={styles.punchSide}>
              {call.side}
            </span>
          </li>
        ))}
      </ul>
      <RowList rows={CALLS_PAGE.power} />
    </>
  );
}

function CuesPage({ accessibilityTextSize }: { readonly accessibilityTextSize: boolean }) {
  return (
    <>
      <PageHeader
        eyebrow={CUES_PAGE.eyebrow}
        title={CUES_PAGE.title}
        detail={CUES_PAGE.detail}
        accessibilityTextSize={accessibilityTextSize}
      />
      <ul
        className={styles.timeline}
        data-ff-stacked={String(accessibilityTextSize)}
        data-testid="onboarding.timeline"
      >
        {CUES_PAGE.timeline.map((entry) => (
          <li key={entry.moment} className={styles.timelineItem}>
            <Text variant="caption2" tone="tertiary" uppercase as="div">
              {entry.moment}
            </Text>
            <Text variant="callout" as="div">
              {entry.channel}
            </Text>
          </li>
        ))}
      </ul>
      <section className={styles.note} data-testid="onboarding.background">
        <Text variant="headline" as="h2">
          {CUES_PAGE.backgroundTitle}
        </Text>
        <Text variant="subheadline" tone="secondary" as="p">
          {CUES_PAGE.backgroundBody}
        </Text>
      </section>
      <Text variant="footnote" tone="tertiary" as="p">
        {CUES_PAGE.footnote}
      </Text>
    </>
  );
}

function SafetyPage({ accessibilityTextSize }: { readonly accessibilityTextSize: boolean }) {
  return (
    <>
      <PageHeader
        eyebrow={SAFETY_PAGE.eyebrow}
        title={SAFETY_PAGE.title}
        detail={SAFETY_PAGE.detail}
        accessibilityTextSize={accessibilityTextSize}
      />
      <RowList rows={SAFETY_PAGE.rows} />
      <Text variant="callout" as="p">
        {SAFETY_PAGE.closing}
      </Text>
    </>
  );
}

export function OnboardingFlow({ onComplete }: OnboardingFlowProps): ReactNode {
  const [page, setPage] = useState(0);
  const accessibilityTextSize = useAccessibilityTextSize();
  const isLast = page === ONBOARDING_PAGE_COUNT - 1;

  return (
    <div className={styles.gate} data-testid="onboarding">
      <Container as="section" measure="readable" className={styles.page}>
        <VisuallyHidden>{ONBOARDING_TITLE}</VisuallyHidden>
        {page === 0 ? <FormatPage accessibilityTextSize={accessibilityTextSize} /> : null}
        {page === 1 ? <CallsPage accessibilityTextSize={accessibilityTextSize} /> : null}
        {page === 2 ? <CuesPage accessibilityTextSize={accessibilityTextSize} /> : null}
        {page === 3 ? <SafetyPage accessibilityTextSize={accessibilityTextSize} /> : null}

        <footer className={styles.footer}>
          {/* F-NAV-22: Back is hidden on page 1, not disabled — a control that
              is present and does nothing is the §8.6 defect this product does
              not ship. */}
          {page > 0 ? (
            <Button
              variant="secondary"
              testId="onboarding.back"
              onPress={() => {
                setPage((current) => current - 1);
              }}
            >
              {ONBOARDING_FOOTER.back}
            </Button>
          ) : null}

          <div className={styles.indicator} data-testid="onboarding.indicator">
            {Array.from({ length: ONBOARDING_PAGE_COUNT }, (_unused, index) => (
              <button
                key={index}
                type="button"
                className={index === page ? `${styles.dot} ${styles.dotActive}` : styles.dot}
                aria-label={pageIndicatorLabel(index)}
                aria-current={index === page ? "true" : undefined}
                data-testid={`onboarding.page.${index + 1}`}
                onClick={() => {
                  setPage(index);
                }}
              />
            ))}
          </div>

          <Button
            variant="secondary"
            testId="onboarding.skip"
            onPress={onComplete}
          >
            {ONBOARDING_FOOTER.skip}
          </Button>

          <Button
            testId="onboarding.advance"
            onPress={() => {
              if (isLast) onComplete();
              else setPage((current) => current + 1);
            }}
          >
            {isLast ? ONBOARDING_FOOTER.finish : ONBOARDING_FOOTER.advance}
          </Button>
        </footer>
      </Container>
    </div>
  );
}
